// LAS PANTALLAS DE LA PROPIA APP SE ABREN DENTRO DE LA APP (t315, 20-oct-2026)
//
// El dueño (19-oct): «En la app de iOS: cuando estás en Comunicación y le das a ver ficha completa, te saca de la app.»
//
// POR QUÉ PASABA. «Ver ficha completa» hacía `window.open('/pacientes?paciente=…', '_blank')`. En un navegador eso es otra
// pestaña, y en el escritorio es justo lo que se quiere: no se pierde la conversación. Pero la app del iPhone (Capacitor) NO
// TIENE PESTAÑAS: la ventana nueva se la pasa al sistema, que la abre en Safari, fuera de la app y sin la sesión (allí hay
// que volver a entrar). La web instalada en la pantalla de inicio hace lo mismo, y en Android la ventana nueva recarga la
// app entera. No era solo ese botón: eran veintiocho sitios, todos con la misma trampa.
//
// LA REGLA, para cualquier pantalla de la app que se abría en otra pestaña:
//   · donde HAY pestañas (cualquier navegador, también el del móvil), se sigue abriendo en otra, como decidió cada pantalla;
//   · donde NO las hay (la app del móvil y la web instalada), se navega DENTRO, con el enrutador y sin recargar nada.
//   · Un enlace de verdad (<a href>) conserva el gesto del navegador: Ctrl o Cmd+clic y el botón central abren otra pestaña
//     (lo mismo que el nº de historial del chat: components/chat/MensajeChat, chat-historial-enlace §10).
// Lo que es de fuera (otra web, un PDF, una descarga, /api/…, /uploads/…) no pasa por aquí: se abre como siempre.
// La guardia que impide que vuelva: backend/routes/rutas-internas-no-salen-de-la-app.test.js, un censo de los
// `target="_blank"` y `window.open` de frontend/src.
//
// SIN IMPORTS DE REACT NI DEL ENRUTADOR A PROPÓSITO: es lógica pura, y así las guardias lo cargan de verdad. El enrutador
// lo deja aquí App.jsx (`registrarNavegador`), una sola vez, por dentro del <BrowserRouter>.
import { enLaAppNativa } from './verDocumento'

// La ventana del navegador. `globalThis.window` y no `window` a secas: en el navegador son la misma, y así las guardias, que
// cargan este fichero con una ventana suya de mentira, ven la que ponen ellas.
function laVentana(ventana) {
  if (ventana) return ventana
  try { return (typeof globalThis !== 'undefined' && globalThis.window) || null } catch (_) { return null }
}

// La web instalada en la pantalla de inicio (iPhone: `navigator.standalone`; el resto: display-mode standalone). Como la app
// nativa, no tiene pestañas: una ventana nueva sale al navegador del teléfono.
function instaladaEnInicio(w) {
  try {
    if (w && w.navigator && w.navigator.standalone === true) return true
    return !!(w && typeof w.matchMedia === 'function' && w.matchMedia('(display-mode: standalone)').matches)
  } catch (_) { return false }
}

/** ¿Este aparato tiene pestañas? No en la app del móvil ni en la web instalada. Sin ventana que mirar: sí (lo de siempre). */
export function hayPestanas(ventana) {
  const w = laVentana(ventana)
  if (!w) return true
  if (enLaAppNativa(w)) return false
  return !instaladaEnInicio(w)
}

// Lo que NO es una pantalla de la app aunque empiece por «/»: el servidor, los ficheros subidos y cualquier fichero con
// extensión (una plantilla .csv, un .pdf). Eso lo sirve el servidor y no el enrutador.
const DEL_SERVIDOR = /^\/(?:api|uploads|assets|media|static|files)(?:[/?#]|$)/i
const CON_EXTENSION = /\.[a-z0-9]{2,5}$/i

/** ¿Es una pantalla de esta app? Una ruta que empieza por UNA barra («//otra.web» es de fuera) y que no sirve el servidor. */
export function esRutaInterna(destino) {
  if (typeof destino !== 'string') return false
  const d = destino.trim()
  if (!/^\/(?![/\\])/.test(d)) return false
  if (DEL_SERVIDOR.test(d)) return false
  const camino = d.split(/[?#]/)[0]
  return !CON_EXTENSION.test(camino)
}

// EL ENRUTADOR DE LA APP. Lo deja App.jsx al montar; sin él (una guardia, una pantalla suelta) se navega con la dirección,
// que en la app también se queda dentro, solo que recargando.
let navegadorGlobal = null
/** App.jsx deja aquí el `navigate` del enrutador. Devuelve la función que lo quita. */
export function registrarNavegador(fn) {
  navegadorGlobal = typeof fn === 'function' ? fn : null
  const mio = navegadorGlobal
  return () => { if (navegadorGlobal === mio) navegadorGlobal = null }
}

/** ¿El clic pide otra pestaña? Ctrl o Cmd (o Mayús/Alt), o el botón central. */
export function clicConModificador(e) {
  return !!(e && (e.button === 1 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey))
}

/** Ir a esa pantalla DENTRO: con el enrutador si lo hay; si no, con la dirección. */
export function irDentro(destino, { navegar, ventana } = {}) {
  const ir = typeof navegar === 'function' ? navegar : navegadorGlobal
  if (ir) { ir(destino); return 'dentro' }
  const w = laVentana(ventana)
  try { if (w && w.location && typeof w.location.assign === 'function') { w.location.assign(destino); return 'dentro' } } catch (_) { /* sin ventana */ }
  return null
}

/**
 * Abrir una pantalla de la app desde un botón. `nuevaPestana`: lo que esa pantalla quiere donde hay pestañas (casi siempre
 * sí: por eso usaba window.open). `rasgos`: el tercer argumento de window.open que ya llevaba (p. ej. 'noopener').
 * Devuelve 'pestana' (se pidió otra pestaña al navegador), 'dentro' (se navegó dentro) o null (no había cómo).
 */
export function abrirRutaInterna(destino, { nuevaPestana = true, rasgos, navegar, ventana } = {}) {
  const w = laVentana(ventana)
  if (nuevaPestana && hayPestanas(w)) {
    try { if (w && typeof w.open === 'function') { if (rasgos) w.open(destino, '_blank', rasgos); else w.open(destino, '_blank') } } catch (_) { /* sin ventanas: lo dice el navegador */ }
    return 'pestana'
  }
  return irDentro(destino, { navegar, ventana: w })
}

/**
 * Las props de un ENLACE a una pantalla de la app, para un <a> o un <Boton as="a">: `{ href, target?, rel?, onClick }`.
 *   · con pestañas y `nuevaPestana`: target="_blank", lo abre el navegador (Ctrl+clic y botón central, también);
 *   · con pestañas y sin `nuevaPestana`: el clic normal navega dentro y el clic con Ctrl o central abre otra pestaña;
 *   · sin pestañas (la app): cualquier clic navega dentro. Nunca un target: sería salir de la app.
 */
export function enlaceInterno(destino, { nuevaPestana = true, rel = 'noopener noreferrer', navegar, ventana } = {}) {
  const w = laVentana(ventana)
  const pestanas = hayPestanas(w)
  const props = { href: destino }
  if (pestanas && nuevaPestana) { props.target = '_blank'; props.rel = rel }
  props.onClick = (e) => {
    if (e && e.defaultPrevented) return
    if (pestanas && (nuevaPestana || clicConModificador(e))) return   // lo hace el navegador: otra pestaña
    if (e && typeof e.preventDefault === 'function') e.preventDefault()
    irDentro(destino, { navegar, ventana: w })
  }
  return props
}

/**
 * Un enlace que puede ser de fuera o de dentro (el «Saber más» de una novedad, que admite «/agenda» o una web): si es una
 * pantalla de la app, como `enlaceInterno`; si no, a otra pestaña como siempre.
 */
export function enlaceDondeSea(destino, opciones = {}) {
  if (esRutaInterna(destino)) return enlaceInterno(destino, opciones)
  return { href: destino, target: '_blank', rel: opciones.rel || 'noopener noreferrer' }
}
